/**
 * "Förslag på nästa steg" (pedagogy spec §7.3.8): at most two suggestions from
 * `suggestNext`, each with a title, a badge, one line of text and a "Starta". A
 * student must never end up with a suggestion and nothing to click (pedagogy
 * review A1), so every row this card renders has a "Starta".
 *
 * Where "Starta" goes is one rule, `suggestionHref` in `@shared/examWrapper` (the
 * §4.3.9 deep-link contract), which Today's extras use too: `which-principle` and
 * `timed-pair` start their sets by themselves (`/practice?set=…`, step 2.8c, with
 * the exam session as provenance), `setup`/`accuracy` start a short batch of
 * open/numeric problems on their topics (`types`, `count=4`, `primary=1`).
 * `START_HREF` is the one map from a suggestion kind to that link.
 *
 * "Lägg till i dagens pass" (E7) asks the page to save the causes with
 * `addToToday`; Today then serves these same suggestions for a week. It is shown
 * while there is something to add and turns into a status once it is done.
 *
 * `problems`/`causes` are only used to tell "no causes chosen yet" apart from
 * "every chosen cause is Vet inte" (`onlyUnknownCauses`, shared with
 * `PatternCard`) when there is nothing to suggest — the student has already
 * answered, so the card must not ask them to choose causes.
 */
import { useId } from "react";
import { Link } from "react-router";
import {
  onlyUnknownCauses,
  suggestionHref,
  TIMED_PAIR_POINTS,
  type CauseMap,
  type Suggestion,
  type WrapperProblem,
} from "@shared/examWrapper";
import { Badge, Button, Card } from "@client/components/ui";
import { useLang, useT, useTp } from "@client/i18n";
import { formatList } from "@client/lib/format";

/** Where each suggestion's "Starta" goes; null means the row has no "Starta" (none today). */
export const START_HREF: Record<
  Suggestion["kind"],
  (suggestion: Suggestion, examSessionId?: string) => string | null
> = {
  "which-principle": (suggestion, examSessionId) => suggestionHref(suggestion, examSessionId),
  "timed-pair": (suggestion, examSessionId) => suggestionHref(suggestion, examSessionId),
  setup: (suggestion, examSessionId) => suggestionHref(suggestion, examSessionId),
  accuracy: (suggestion, examSessionId) => suggestionHref(suggestion, examSessionId),
};

export interface SuggestedNextCardProps {
  suggestions: readonly Suggestion[];
  problems: readonly WrapperProblem[];
  causes: CauseMap;
  /** The localised name of an active topic id. */
  topicName: (topicId: string) => string;
  /** The graded exam the suggestions come from; the sets carry it as provenance. */
  examSessionId?: string;
  /** "Lägg till i dagens pass" (E7); the button is left out without a handler. */
  onAddToToday?: () => void;
  /** The save that stamps the exam as added is on its way. */
  adding?: boolean;
  /** The exam was added to Today's session (the button turns into a status). */
  added?: boolean;
}

export function SuggestedNextCard({
  suggestions,
  problems,
  causes,
  topicName,
  examSessionId,
  onAddToToday,
  adding = false,
  added = false,
}: SuggestedNextCardProps) {
  const t = useT();
  const visible = suggestions;
  // When every chosen cause is "Vet inte" there is nothing to suggest, but the
  // student did choose — don't ask them to (pedagogy review, batch C fix 1).
  const onlyUnknown = onlyUnknownCauses(problems, causes);
  return (
    <Card
      title={t("exam.wrapper.next.title")}
      description={visible.length > 0 ? t("exam.wrapper.next.description") : undefined}
    >
      {visible.length === 0 ? (
        <p className="text-sm text-ink-700 dark:text-ink-200">
          {t(onlyUnknown ? "exam.wrapper.next.onlyUnknown" : "exam.wrapper.next.none")}
        </p>
      ) : (
        <ul className="space-y-3">
          {visible.map((suggestion) => (
            <SuggestionRow
              key={suggestion.kind}
              suggestion={suggestion}
              topicName={topicName}
              {...(examSessionId ? { examSessionId } : {})}
            />
          ))}
        </ul>
      )}
      {visible.length > 0 && onAddToToday && (
        <div className="mt-4 flex flex-wrap items-center gap-3">
          {/* Stays a (focusable) button once done, so focus is not lost; it just stops acting. */}
          <Button
            variant="secondary"
            loading={adding}
            aria-disabled={added || undefined}
            onClick={added ? undefined : onAddToToday}
          >
            {t(added ? "exam.wrapper.next.added" : "exam.wrapper.next.addToToday")}
          </Button>
          {/* Always mounted, so the change is spoken. */}
          <p role="status" className="sr-only">
            {added ? t("exam.wrapper.next.added") : ""}
          </p>
        </div>
      )}
    </Card>
  );
}

function SuggestionRow({
  suggestion,
  topicName,
  examSessionId,
}: {
  suggestion: Suggestion;
  topicName: (topicId: string) => string;
  examSessionId?: string;
}) {
  const t = useT();
  const tp = useTp();
  const { lang } = useLang();
  const titleId = useId();
  const href = START_HREF[suggestion.kind](suggestion, examSessionId);
  const topics = (ids: readonly string[]) => formatList(ids.map(topicName), lang);

  let title: string;
  let body: string;
  let badge: string;
  switch (suggestion.kind) {
    case "which-principle":
      title = t("exam.wrapper.next.whichPrinciple.title");
      body = tp("exam.wrapper.next.whichPrinciple.body", suggestion.count, {
        chapters: tp("exam.wrapper.chapters", suggestion.chapters.length, {
          list: formatList(suggestion.chapters.map(String), lang),
        }),
        minutes: suggestion.minutes,
      });
      badge = t("exam.wrapper.next.badge.interleaved");
      break;
    case "timed-pair":
      title = t("exam.wrapper.next.timedPair.title");
      body = t("exam.wrapper.next.timedPair.body", {
        total: TIMED_PAIR_POINTS,
        minutes: suggestion.minutes,
      });
      badge = t("exam.wrapper.next.badge.pacing");
      break;
    case "setup":
      title = t("exam.wrapper.next.setup.title");
      body = t("exam.wrapper.next.setup.body", { topics: topics(suggestion.topics) });
      badge = t("exam.wrapper.next.badge.setup");
      break;
    case "accuracy":
      title = t("exam.wrapper.next.accuracy.title");
      body = t("exam.wrapper.next.accuracy.body", { topics: topics(suggestion.topics) });
      badge = t("exam.wrapper.next.badge.accuracy");
      break;
  }

  return (
    <li className="rounded-xl border border-ink-200 p-4 dark:border-ink-800">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 id={titleId} className="font-medium text-ink-900 dark:text-ink-50">
          {title}
        </h3>
        <Badge tone="accent">{badge}</Badge>
      </div>
      <p className="mt-1 text-sm text-ink-700 dark:text-ink-200">{body}</p>
      {href && (
        <Link
          to={href}
          aria-describedby={titleId}
          className="mt-3 inline-flex min-h-11 items-center rounded-lg bg-accent-600 px-4 py-2 text-sm font-medium text-white hover:bg-accent-700 dark:bg-accent-600 dark:hover:bg-accent-700"
        >
          {t("exam.wrapper.next.start")}
        </Link>
      )}
    </li>
  );
}
